import type { BlockAlias, BlockName } from '@core/block';
import { fireAndForget } from '@core/util/fireAndForget';
import { queryReadyGate } from '@queries/gate';
import { useItemRawName } from '@queries/preview';
import {
  useDocumentAccessLevelQuery,
  useDocumentMetadataQuery,
} from '@queries/storage/document-metadata';
import { type DialogHandle, type ManagedDialogInput, openDialog } from '@ui';
import {
  type Accessor,
  type ComponentProps,
  createEffect,
  createMemo,
  createSignal,
  getOwner,
  Suspense,
} from 'solid-js';
import { getPermissions } from '../SharePermissions';
import { ShareModal } from './ShareButton';

type ShareModalProps = ComponentProps<typeof ShareModal>;

/** Props callers provide when opening the share modal. */
export type ShareModalInput = ManagedDialogInput<ShareModalProps>;

function SuspendedShareModal(props: ShareModalProps) {
  return (
    <Suspense>
      <ShareModal {...props} />
    </Suspense>
  );
}

/** Opens the share modal outside of any component, e.g. from a list action. */
export function openShareModal(props: ShareModalInput): DialogHandle {
  return openDialog(SuspendedShareModal, props, {
    key: `share:${props.itemType}:${props.id}`,
  });
}

/**
 * Returns an opener for a share modal owned by the calling component. The
 * modal follows `props` while open and closes when the component unmounts.
 * Return `undefined` from `props` until the share data has loaded; opening
 * before then waits for it, so the modal never shows placeholder permissions.
 * `onClose` runs when the person closes an opened modal, not when a newer
 * one replaces it or its owner unmounts.
 */
export function useShareModal(
  props: Accessor<ShareModalInput | undefined>,
  options: { onClose?: () => void } = {}
): () => void {
  const owner = getOwner();
  const [pending, setPending] = createSignal(false);

  const notifyClosed = async (handle: DialogHandle) => {
    const { reason } = await handle.closed;
    if (reason === 'dismissed' || reason === 'programmatic')
      options.onClose?.();
  };

  const openWith = (initial: ShareModalInput) => {
    setPending(false);
    fireAndForget(
      notifyClosed(
        openDialog(SuspendedShareModal, () => props() ?? initial, {
          owner,
          key: `share:${initial.itemType}:${initial.id}`,
        })
      ),
      'share-modal'
    );
  };

  createEffect(() => {
    if (!pending()) return;
    const initial = props();
    if (initial) openWith(initial);
  });

  return () => {
    const initial = props();
    if (initial) openWith(initial);
    else setPending(true);
  };
}

/**
 * Returns an opener for a document's share modal when only its id is known,
 * e.g. from a view header outside the document.
 */
export function useDocumentShareModal(
  target: Accessor<
    | {
        documentId: string;
        blockAlias: BlockName | BlockAlias;
        copyLink?: () => void;
      }
    | undefined
  >
): () => void {
  const documentId = () => target()?.documentId ?? '';
  const currentName = createMemo(() => {
    const id = documentId();
    return id ? useItemRawName(() => ({ id, type: 'document' })) : undefined;
  });
  const metadataQuery = useDocumentMetadataQuery(documentId);
  const accessLevelQuery = useDocumentAccessLevelQuery(documentId);

  return useShareModal(() => {
    const current = target();
    if (
      !current ||
      !queryReadyGate(metadataQuery) ||
      !queryReadyGate(accessLevelQuery)
    ) {
      return;
    }
    return {
      id: current.documentId,
      blockAlias: current.blockAlias,
      itemType: 'document',
      name: currentName()?.() ?? metadataQuery.data.documentName,
      owner: metadataQuery.data.owner,
      userPermissions: getPermissions(accessLevelQuery.data),
      copyLink: current.copyLink,
    };
  });
}
